import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Plan, useCreatePaymentMutation, useInvoicePlanPriceQuery, useUserQuery } from '../../../../api/gql';
import { BillingEnterpriseBox } from '../../../../components/BillingEnterpriseBox';
import { BillingPlans } from '../../../../components/BillingPlans';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { PageHeader } from '../../../../components/PageHeader';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';
import { PageContent, PageLayout } from '../../../../layout';
import { PaymentModal } from '../../../../modules/payment/PaymentModal';
import { notify } from '../../../../utils/notification';
import { fromCentesimal } from '../../../../utils/transform';

const TRANS_NS = 'billing';

export const InvoiceBilling = () => {
  const { invoiceId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();
  const [clientPaymentSecret, setClientPaymentSecret] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const handleQueryError = (error: unknown) => {
    console.error(error);
    notify.negative({ message: t('query.error') });
    safeNavigate(-1);
  };

  const handleMutationError = (error: unknown) => {
    console.error(error);
    notify.negative({ message: t('mutation.error') });
  };

  const { data: userData, isLoading: isLoadingUserQuery, error } = useUserQuery();

  const {
    data: invoiceData,
    isLoading: isLoadingInvoiceQuery,
    error: planPriceQueryError,
  } = useInvoicePlanPriceQuery({
    id: invoiceId ?? '',
  });

  useEffect(() => {
    if (error || planPriceQueryError) {
      console.error(error);
      handleQueryError(error || planPriceQueryError);
    }
  }, [error, planPriceQueryError, handleQueryError]);

  const { mutate: createPaymentMutate, isPending: isMutating } = useCreatePaymentMutation({
    onSuccess: ({ createPayment: { paymentRequired, clientSecret } }) => handleSuccess(paymentRequired, clientSecret),
    onError: handleMutationError,
  });

  const handleSuccess = (paymentRequired: boolean, clientSecret?: string | null) => {
    if (paymentRequired && clientSecret) {
      setClientPaymentSecret(clientSecret);
      setPaymentModalOpen(true);
      return;
    }
    safeNavigate(`/upload/invoice/${invoiceId}`, { replace: true });
    notify.positive({ message: t('mutation.success') });
  };

  const onPlanChoose = (plan: Plan) => {
    if (!invoiceId) {
      throw new Error('');
    }
    if (clientPaymentSecret) {
      setPaymentModalOpen(true);
      return;
    }
    createPaymentMutate({ id: invoiceId, plan: plan });
  };

  const isLoading = useMemo(
    () => isLoadingInvoiceQuery || isLoadingUserQuery,
    [isLoadingUserQuery, isLoadingInvoiceQuery],
  );
  const isFreePlanEnabled = useMemo(() => invoiceData?.invoice.trialPrice !== null, [invoiceData?.invoice.trialPrice]);

  return (
    <PageLayout>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />
      <PageContent sx={{ pt: 8, alignItems: 'center' }}>
        {isLoading ? <FullSizeLoader /> : null}

        {invoiceData && !userData?.user.isEnterprise ? (
          <BillingPlans
            onPlanChoose={onPlanChoose}
            contact={{
              missingPhone: !invoiceData.invoice.customer?.phoneNumber,
              missingEmail: !invoiceData.invoice.customer?.email,
            }}
            standardPrice={fromCentesimal(invoiceData.invoice.standardPrice ?? 0)}
            standardDiscount={fromCentesimal(invoiceData.invoice.standardDiscount ?? 0)}
            isFreePlanEnabled={isFreePlanEnabled}
          />
        ) : null}

        {userData?.user.isEnterprise ? (
          <Box display={'flex'} flex={'1 1 0'} alignItems={'flex-start'} justifyContent={'center'} height={'100%'}>
            <BillingEnterpriseBox handleSubmit={() => onPlanChoose(Plan.Standard)} isLoading={isMutating} />
          </Box>
        ) : null}
      </PageContent>

      <PaymentModal
        returnUrl={`${window.location.origin}/upload/invoice/${invoiceId ?? ''}/payment-result`}
        clientSecret={clientPaymentSecret}
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
      />
    </PageLayout>
  );
};
